import type { WorkflowTriggerType, WorkflowActionType } from "@/model/interface/Workflow";

export const TRIGGER_LABELS: Record<WorkflowTriggerType, string> = {
  NEW_CONTACT: "New Contact Created",
  DEAL_STAGE_CHANGE: "Deal Stage Changed",
  TAG_ADDED: "Tag Added to Contact",
  INCOMING_CALL: "Incoming Call Received",
  INCOMING_SMS: "Incoming SMS Received",
  TASK_COMPLETED: "Task Completed",
  TASK_REMINDER: "Task Reminder Due",
  LEAD_STALE: "Lead Went Stale",
  WEBHOOK_RECEIVED: "Webhook Received",
  CALL_DISPOSITION: "Call Disposition Set",
  VOICEMAIL_RECEIVED: "Voicemail Received",
  CONTACT_UPDATED: "Contact Updated",
  FORM_SUBMISSION: "Form Submitted",
  NEW_USER: "New User",
  MEMBER_INVITED: "Member Invited",
  LEAD_CONVERTED: "Lead Converted to Contact",
  PROPERTY_FIELD_CHANGED: "Property Field Changed",
};

const PICKER_TRIGGERS: WorkflowTriggerType[] = [
  "NEW_CONTACT",
  "DEAL_STAGE_CHANGE",
  "LEAD_CONVERTED",
  "TAG_ADDED",
  "INCOMING_CALL",
  "INCOMING_SMS",
  "TASK_COMPLETED",
  "TASK_REMINDER",
  "LEAD_STALE",
  "WEBHOOK_RECEIVED",
  "CALL_DISPOSITION",
  "VOICEMAIL_RECEIVED",
];

export const TRIGGER_OPTIONS: { value: WorkflowTriggerType; label: string }[] = PICKER_TRIGGERS.map((triggerType) => ({
  value: triggerType,
  label: TRIGGER_LABELS[triggerType],
}));

export const ACTION_LABELS: Record<WorkflowActionType, string> = {
  SEND_SMS: "Send SMS",
  SEND_EMAIL: "Send Email",
  CREATE_TASK: "Create Task",
  ADD_TAG: "Add Tag",
  REMOVE_TAG: "Remove Tag",
  MOVE_DEAL_STAGE: "Move Deal Stage",
  ASSIGN_TO_MEMBER: "Assign to Member",
  SEND_NOTIFICATION: "Send Notification",
  SEND_PUSH: "Send Push",
  SEND_WEBHOOK: "Send Webhook",
  ENQUEUE_POWER_DIAL: "Add to campaign",
  PLACE_AI_CALL: "Place AI Call",
  TRANSCRIBE_VOICEMAIL: "Transcribe Voicemail",
  NOTIFY_VOICEMAIL: "Notify Voicemail Owner",
};

const GENERAL_ACTIONS: WorkflowActionType[] = [
  "SEND_SMS",
  "SEND_EMAIL",
  "CREATE_TASK",
  "ADD_TAG",
  "REMOVE_TAG",
  "MOVE_DEAL_STAGE",
  "ASSIGN_TO_MEMBER",
  "SEND_NOTIFICATION",
  "SEND_PUSH",
  "SEND_WEBHOOK",
  "ENQUEUE_POWER_DIAL",
  "PLACE_AI_CALL",
];

const VOICEMAIL_ACTIONS: WorkflowActionType[] = ["TRANSCRIBE_VOICEMAIL", "NOTIFY_VOICEMAIL"];

export function actionOptionsFor(triggerType: WorkflowTriggerType): { value: WorkflowActionType; label: string }[] {
  const actionTypes = triggerType === "VOICEMAIL_RECEIVED" ? [...VOICEMAIL_ACTIONS, ...GENERAL_ACTIONS] : GENERAL_ACTIONS;
  return actionTypes.map((actionType) => ({ value: actionType, label: ACTION_LABELS[actionType] }));
}

// Actions that automatically contact the lead. In reminder workflows these are
// gated behind the org's `remindersAutoOutreachEnabled` toggle (off by default).
export const AUTO_OUTREACH_DESCRIPTIONS: Partial<Record<WorkflowActionType, string>> = {
  SEND_SMS: "Auto-texts the lead",
  SEND_EMAIL: "Auto-emails the lead",
  PLACE_AI_CALL: "Auto-calls the lead with an AI agent",
};

export const REMINDER_TRIGGERS: WorkflowTriggerType[] = ["TASK_REMINDER", "LEAD_STALE"];

export function formatDelay(minutes: number): string {
  if (minutes === 0) return "Immediately";
  if (minutes < 60) return `After ${minutes} min`;
  if (minutes < 1440) return `After ${Math.round(minutes / 60)} hr`;
  return `After ${Math.round(minutes / 1440)} day${Math.round(minutes / 1440) !== 1 ? "s" : ""}`;
}
